'use client';

import { useCallback } from 'react';
import { isRtlLocale } from '@/shared/constants/locales';
import { setLocaleCookie } from '@/shared/lib/locale-cookie';
import { useAuthStore } from '@/features/auth';
import { useUpdateLocale } from './useUpdateLocale';

/**
 * Switch the app's interface language: the user's profile (when signed in),
 * the locale cookie, the document direction, then a reload so every server
 * and client string follows. One path for every entry point (the language
 * selector, the AI architect's offer), so they cannot drift.
 */
export function useSwitchAppLocale() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { updateLocale, isUpdating } = useUpdateLocale();

  const switchLocale = useCallback(
    async (newLocale: string): Promise<void> => {
      // Update dir attribute immediately for RTL support (before reload)
      document.documentElement.setAttribute(
        'dir',
        isRtlLocale(newLocale) ? 'rtl' : 'ltr'
      );

      // Guests can't call PUT /v1/user/locale (requires auth) — cookie is enough.
      // Post-login sync will push the cookie value into the user's profile.
      if (isAuthenticated && !(await updateLocale(newLocale))) {
        return;
      }

      // Save to cookie (with proper domain for subdomain sharing)
      setLocaleCookie(newLocale);

      // An explicit pick IS the decision — silence the suggestion banner for good
      localStorage.setItem('language-banner-dismissed', 'true');

      // Reload page to apply new locale
      window.location.reload();
    },
    [isAuthenticated, updateLocale]
  );

  return { switchLocale, isUpdating };
}
